import Splide from '@splidejs/splide';

const ROOT_SELECTOR = '[data-project-screens]';

// Карусель экранов кейса: четыре в ряд от 1440, ниже — меньше.
// Тот же Splide, что в отзывах; подписи стрелок — из data-атрибутов.
const BREAKPOINTS = {
    1439: { perPage: 3 },
    1023: { perPage: 2 },
    767: { perPage: 1 },
};

function initCarousel(root) {
    const labels = root.dataset;

    new Splide(root, {
        type: 'slide',
        perPage: 4,
        perMove: 1,
        gap: 'var(--spacing-3)',
        speed: 450,
        easing: 'cubic-bezier(.16, 1, .3, 1)',
        arrows: true,
        pagination: false,
        rewind: false,
        breakpoints: BREAKPOINTS,
        i18n: {
            carousel: labels.labelCarousel,
            prev: labels.labelPrev,
            next: labels.labelNext,
            slideX: labels.labelSelect,
        },
    }).mount();
}

// Лайтбокс на нативном <dialog>: открытие по клику на скриншот, листание
// стрелками и клавишами, закрытие по Esc, кнопке и клику по подложке.
// Фокус после закрытия возвращается на открывшую миниатюру.
function initLightbox(section) {
    const dialog = section.querySelector('[data-lightbox]');

    if (!dialog || typeof dialog.showModal !== 'function') {
        return;
    }

    const slides = Array.from(dialog.querySelectorAll('[data-lightbox-slide]'));
    const counter = dialog.querySelector('[data-lightbox-counter]');
    const triggers = Array.from(section.querySelectorAll('[data-lightbox-open]'));
    const closeButton = dialog.querySelector('[data-lightbox-close]');
    const stage = dialog.querySelector('[data-lightbox-stage]');
    const zoomButton = dialog.querySelector('[data-lightbox-zoom]');

    let current = 0;
    let opener = null;

    // Зум: картинка в натуральную ширину, сцена прокручивается. Сбрасывается
    // при смене слайда и при закрытии — следующий скриншот может быть и так
    // целиком в экране.
    const setZoom = (zoomed) => {
        if (!stage) {
            return;
        }

        stage.classList.toggle('is-zoomed', zoomed);
        stage.scrollTop = 0;

        if (zoomButton) {
            zoomButton.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
            zoomButton.setAttribute('aria-label', zoomed ? zoomButton.dataset.labelOut : zoomButton.dataset.labelIn);
        }
    };

    const isZoomed = () => Boolean(stage && stage.classList.contains('is-zoomed'));

    const show = (index) => {
        current = (index + slides.length) % slides.length;

        slides.forEach((slide, i) => {
            slide.hidden = i !== current;
        });

        // Полноразмерная картинка слайда грузится при первом показе (и у соседей —
        // чтобы стрелки не ждали сеть). До этого в разметке только data-src.
        [current, current + 1, current - 1].forEach((i) => {
            const slide = slides[(i + slides.length) % slides.length];
            const image = slide && slide.querySelector('img[data-src]');

            if (image) {
                image.src = image.dataset.src;
                image.removeAttribute('data-src');
            }
        });

        if (counter) {
            counter.textContent = `${current + 1} / ${slides.length}`;
        }

        setZoom(false);
    };

    const open = (index, trigger) => {
        opener = trigger;
        show(index);
        dialog.showModal();
        document.documentElement.classList.add('has-lightbox');
        closeButton.focus();
    };

    // Уборка после закрытия вынесена отдельно и вызывается и из close(),
    // и из событий диалога: Esc закрывает <dialog> сам, минуя close(),
    // а событие close в некоторых сборках Chrome не приходит — toggle приходит.
    const onClosed = () => {
        document.documentElement.classList.remove('has-lightbox');

        if (opener) {
            opener.focus();
            opener = null;
        }
    };

    const close = () => {
        setZoom(false);
        dialog.close();
        onClosed();
    };

    triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => open(Number(trigger.dataset.lightboxOpen), trigger));
    });

    closeButton.addEventListener('click', close);

    if (zoomButton) {
        zoomButton.addEventListener('click', () => setZoom(!isZoomed()));
    }

    // Клик по самой картинке — тот же переключатель, что лупа.
    slides.forEach((slide) => {
        const image = slide.querySelector('img');

        if (image) {
            image.addEventListener('click', () => setZoom(!isZoomed()));
        }
    });

    dialog.querySelector('[data-lightbox-prev]').addEventListener('click', () => show(current - 1));
    dialog.querySelector('[data-lightbox-next]').addEventListener('click', () => show(current + 1));

    dialog.addEventListener('click', (event) => {
        // Клик по самой подложке (не по содержимому) закрывает.
        if (event.target === dialog) {
            close();
        }
    });

    dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
            show(current - 1);
        } else if (event.key === 'ArrowRight') {
            show(current + 1);
        }
    });

    dialog.addEventListener('close', () => {
        setZoom(false);
        onClosed();
    });
    dialog.addEventListener('toggle', (event) => {
        if (event.newState === 'closed') {
            onClosed();
        }
    });
}

/**
 * «+N more» в стеке полосы фактов: поповер с остальными технологиями под
 * кнопкой. Закрывается повторным кликом, кликом мимо и Esc — фокус после
 * Esc возвращается на кнопку.
 */
const initStackPopover = (stack) => {
    const toggle = stack.querySelector('[data-stack-toggle]');
    const popover = stack.querySelector('[data-stack-popover]');

    if (!toggle || !popover) {
        return;
    }

    const setOpen = (open) => {
        popover.hidden = !open;
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    };

    toggle.addEventListener('click', () => setOpen(popover.hidden));

    document.addEventListener('click', (event) => {
        if (!popover.hidden && !stack.contains(event.target)) {
            setOpen(false);
        }
    });

    stack.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !popover.hidden) {
            setOpen(false);
            toggle.focus();
        }
    });
};

export default function ProjectScreens() {
    document.querySelectorAll('[data-stack]').forEach(initStackPopover);

    const roots = Array.from(document.querySelectorAll(ROOT_SELECTOR));

    if (!roots.length) {
        return;
    }

    roots.forEach((root) => {
        initCarousel(root);
        initLightbox(root.parentElement);
    });
}
